import React from 'react';

import classNames from 'classnames';

import {FocusRing} from '@react-aria/focus';
import {useHover} from '@react-aria/interactions';
import {useTextField} from '@react-aria/textfield';
import {mergeProps} from '@react-aria/utils';

import type {Props} from './types';
import {useInputTextCls} from './use-input-cls';
import {DATE_TIME_TYPES, INPUT_SIZE} from './constants';
import {Description} from './description';
import {ErrorMessage} from './error-message';
import {filterTextInputDomProps} from './filter-text-input-dom-props';
import styles from './styles.module.css';

const LABEL_PINNED_TYPES = DATE_TIME_TYPES;

export const TextInput: React.FunctionComponent<Props> = (props) => {
    const {isDisabled, size = INPUT_SIZE.MEDIUM, label} = props;
    const {onChange, className, ...textFieldProps} = props;
    const inputRef = React.useRef<HTMLInputElement>(null);
    const handleChange = React.useCallback(
        (value: string) => {
            onChange?.(value);
        },
        [onChange],
    );
    const {labelProps, inputProps, descriptionProps, errorMessageProps} = useTextField(
        {...textFieldProps, onChange: handleChange, value: props.value},
        inputRef,
    );
    const {hoverProps, isHovered} = useHover({isDisabled});
    const inputTextCls = useInputTextCls(size);

    const labelPinned = LABEL_PINNED_TYPES.indexOf(props.type) !== -1 || inputProps.value;

    const DescriptionElement = props.errorMessage ? ErrorMessage : Description;
    const showDescription = Boolean(props.description) && !props.errorMessage;
    const showError = Boolean(props.errorMessage);
    const isInvalid = props.validationState === 'invalid';
    const domProps = filterTextInputDomProps(props);
    const limitExceeded = props.limit && props.value ? props.value.length - props.limit : 0;

    return (
        <FocusRing focusClass={styles.focused} within={true} isTextInput={true} autoFocus={props.autoFocus}>
            <div
                className={classNames(
                    className,
                    styles[size],
                    styles.wrapper,
                    isHovered && styles.hovered,
                    (showError || isInvalid) && styles.withError,
                )}
                {...hoverProps}
            >
                <div className={styles.inputContainer}>
                    <input
                        className={classNames(styles.input, inputTextCls)}
                        {...mergeProps(inputProps, domProps)}
                        ref={inputRef}
                    />
                    {limitExceeded > 0 ? (
                        <span className={classNames(styles.limit, inputTextCls)}>{`-${limitExceeded}`}</span>
                    ) : null}
                </div>
                {label ? (
                    <label className={classNames(styles.label, inputTextCls, labelPinned && styles.labelPinned)} {...labelProps}>
                        {label}
                    </label>
                ) : null}
                {showDescription ? <DescriptionElement {...descriptionProps}>{props.description}</DescriptionElement> : null}
                {showError ? <ErrorMessage {...errorMessageProps}>{props.errorMessage}</ErrorMessage> : null}
            </div>
        </FocusRing>
    );
};
